import React, { useState } from 'react';
import { Dialog, Button, Select, Flex, Text } from '@radix-ui/themes';
import DialogCloseButton from './DialogCloseButton';
import axiosInstance from '../../config/axiosConfig';
import { toast } from 'react-toastify';

// Standard hospital emergency color codes
const EMERGENCY_CODES = [
  {
    name: 'Red',
    color: '#dc2626',
    meaning: 'Fire',
    description: 'Fire or smoke detected in the facility'
  },
  {
    name: 'Blue',
    color: '#2563eb',
    meaning: 'Cardiac / Medical Emergency',
    description: 'Adult cardiac or respiratory arrest'
  },
  {
    name: 'Black',
    color: '#1f2937',
    meaning: 'Bomb Threat',
    description: 'Suspected explosive device or threat'
  },
  {
    name: 'Yellow',
    color: '#ca8a04',
    meaning: 'Missing Patient',
    description: 'Patient, resident, or child is missing'
  },
  {
    name: 'Orange',
    color: '#ea580c',
    meaning: 'Hazardous Spill',
    description: 'Chemical, biological, or radioactive spill'
  },
  {
    name: 'Purple',
    color: '#9333ea',
    meaning: 'Hostage / Lockdown',
    description: 'Hostage situation or security lockdown'
  },
  {
    name: 'Pink',
    color: '#ec4899',
    meaning: 'Infant Abduction',
    description: 'Infant or child abduction'
  },
  {
    name: 'White',
    color: '#6b7280',
    meaning: 'Violent / Aggressive Person',
    description: 'Violent, threatening, or aggressive individual'
  },
  {
    name: 'Grey',
    color: '#9ca3af',
    meaning: 'System Failure',
    description: 'Critical infrastructure or system failure'
  },
  {
    name: 'Green',
    color: '#16a34a',
    meaning: 'Evacuation',
    description: 'Evacuation of a ward, floor, or facility'
  },
];

const SOSDialog = ({ isOpen, onClose }) => {
  const [selectedCode, setSelectedCode] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const department = localStorage.getItem('department') || '';

  const selectedCodeData = EMERGENCY_CODES.find((c) => c.name === selectedCode);

  const handleSubmit = async () => {
    if (!selectedCode) {
      setError('Please select an emergency code');
      return;
    }
    if (details.trim().length > 200) {
      setError('Details must be 200 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const sosModel = {
        CodeName: selectedCode,
        color: selectedCodeData.color,
        Meaning: selectedCodeData.meaning,
        Department: department,
        Details: details.trim() || selectedCodeData.description,
      };

      console.log('Sending SOS request:', sosModel);

      const response = await axiosInstance.post('/AlarmManagement/sos', sosModel);

      if (response.data && response.data.result) {
        console.log('SOS sent successfully');
        toast.success('🆘 Emergency SOS alert sent successfully! Medical staff have been notified.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
        handleClose();
      } else {
        setError(response.data?.errorResponse?.message || 'Failed to send SOS');
      }
    } catch (error) {
      console.error('Error sending SOS:', error);
      setError(error.response?.data?.errorResponse?.message || 'Failed to send emergency alert');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedCode('');
    setDetails('');
    setError('');
    setIsSubmitting(false);
    onClose();
  };

  const handleDetailsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 200) {
      setDetails(value);
      setError('');
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content
        style={{ maxWidth: 560, width: '100%' }}
        className="bg-white rounded-lg shadow-xl"
      >
        <DialogCloseButton onClose={handleClose} />
        <Dialog.Title className="text-xl font-bold text-red-600 mb-1">
          Emergency Alert
        </Dialog.Title>
        {department && (
          <Text size="2" color="gray" className="block mb-5">
            Department: <strong>{department}</strong>
          </Text>
        )}

        <Flex direction="column" gap="5">
          {/* Emergency Code Selection */}
          <div>
            <Text size="3" weight="medium" className="text-gray-800 mb-3 block">
              Emergency Code <span className="text-red-500">*</span>
            </Text>
            <Select.Root value={selectedCode} onValueChange={setSelectedCode}>
              <Select.Trigger
                placeholder="Select emergency code"
                className="w-full"
                style={{
                  borderColor: selectedCodeData ? selectedCodeData.color : '#d1d5db',
                  borderWidth: '2px'
                }}
              />
              <Select.Content position="popper" style={{ maxHeight: 320 }}>
                <Select.Group>
                  <Select.Label>Hospital Emergency Codes</Select.Label>
                  {EMERGENCY_CODES.map((code) => (
                    <Select.Item
                      key={code.name}
                      value={code.name}
                      style={{ paddingTop: '10px', paddingBottom: '10px' }}
                    >
                      <Flex align="center" gap="3">
                        <div
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: code.color,
                            flexShrink: 0,
                            border: '1.5px solid rgba(0,0,0,0.15)'
                          }}
                        />
                        <Text size="2" weight="medium">
                          <span style={{ color: '#374151' }}>Code {code.name}</span>
                          <span style={{ color: '#9ca3af', margin: '0 6px' }}>—</span>
                          <span style={{ color: '#6b7280' }}>{code.meaning}</span>
                        </Text>
                      </Flex>
                    </Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </div>

          {/* Selected Code Preview */}
          {selectedCodeData && (
            <div
              className="p-4 rounded-lg border-2"
              style={{
                borderColor: selectedCodeData.color,
                backgroundColor: `${selectedCodeData.color}15`
              }}
            >
              <Flex align="center" gap="3">
                <div
                  className="w-10 h-10 rounded-full border-2 border-white shadow-md flex-shrink-0"
                  style={{ backgroundColor: selectedCodeData.color }}
                />
                <Flex direction="column" gap="1">
                  <Text size="4" weight="bold" style={{ color: selectedCodeData.color }}>
                    Code {selectedCode.toUpperCase()} — {selectedCodeData.meaning}
                  </Text>
                  <Text size="2" color="gray">{selectedCodeData.description}</Text>
                </Flex>
              </Flex>
            </div>
          )}

          {/* Emergency Details (optional) */}
          <div>
            <Flex justify="between" align="center" className="mb-3">
              <Text size="3" weight="medium" className="text-gray-800">
                Additional Details{' '}
                <span className="text-gray-400 font-normal" style={{ fontSize: '0.8em' }}>(optional)</span>
              </Text>
              <Text size="2" color="gray">
                {details.length}/200
              </Text>
            </Flex>
            <textarea
              value={details}
              onChange={handleDetailsChange}
              placeholder={
                selectedCodeData
                  ? selectedCodeData.description
                  : 'Provide additional details if needed'
              }
              className="w-full p-3 border-2 border-gray-300 rounded-lg resize-none focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-colors"
              style={{ minHeight: '80px' }}
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded">
              <Text size="2" color="red" weight="medium">
                {error}
              </Text>
            </div>
          )}

          {/* Action Buttons */}
          <Flex gap="3" justify="end" className="mt-2">
            <Button
              variant="soft"
              color="gray"
              size="3"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="3"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedCode}
              style={{
                backgroundColor: '#dc2626',
                opacity: (isSubmitting || !selectedCode) ? 0.6 : 1
              }}
            >
              {isSubmitting ? 'Sending...' : 'Send Alert'}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default SOSDialog;